import {Component, Inject, OnInit} from '@angular/core';
import {MatDialog, MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Organization} from "../../../../../../model/Organization";
import {ProjectFolders} from "../../../../../../model/supporting/ProjectFolders";
import {Book} from "../../../../../../model/Book";
import {Project} from "../../../../../../model/Project";
import {ProjectService} from "../../../../../service-data/project.service";
import {LanguagePipe} from "../../../../../pipe/language.pipe";

/**
 * "Move Book To A Different Project".
 *
 * This dialog was a verbatim copy of `ReasignDraftComponent` and had
 * never worked. It read `data.draft` while both call sites — the
 * project thumbnail and the book thumbnail — pass `data.book`, so
 * `this.draft` was always `undefined` and the template's
 * `{{ draft.title }}` threw as soon as the dialog opened. It then
 * called `DraftService.reassignProject(...)` — the DRAFT endpoint —
 * with what it believed was a Draft id.
 *
 * It now takes the Book and moves it with
 * `ProjectService.moveBookToProject`, the same endpoint Organize
 * Projects uses for exactly this operation.
 */
@Component({
  selector: 'app-reasign-book',
  templateUrl: './reasign-book.component.html',
  styleUrls: ['./reasign-book.component.css']
})

export class ReasignBookComponent implements OnInit {
  public breakpoint: number; // Breakpoint observer code
  public book: Book;
  public allProjects: Project[];
  public hasFolders: boolean;

  organization: Organization;
  folders: ProjectFolders;

  filterByFolder = false;
  selectedProject: any = null;
  selectedFolderNode: any = null;

  /** True while the move is in flight. */
  moving = false;

  constructor(public dialog: MatDialog, private projectService: ProjectService,
              private snackBar: MatSnackBar,
              @Inject(MAT_DIALOG_DATA) data, private dialogRef: MatDialogRef<ReasignBookComponent>) {
    this.book = data.book;
    this.allProjects = data.projects;
    this.folders = data.folders;
    this.hasFolders = !!(data.folders && data.folders.getFlattened && data.folders.getFlattened().length > 0);
  }

  public ngOnInit(): void {
    this.breakpoint = window.innerWidth <= 600 ? 1 : 2; // Breakpoint observer code
  }

  // tslint:disable-next-line:no-any
  public onResize(event: any): void {
    this.breakpoint = event.target.innerWidth <= 600 ? 1 : 2;
  }

  handleSelection(name: string) {
    this.selectedFolderNode = name;
    this.selectedProject = null;
  }

  onToggleChange() {
    this.selectedProject = null; // Clear selection to prevent hidden-state errors
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  /** Id of the Project this Book is in now, or 0 when it is an orphan. */
  get currentProjectId(): number {
    if ( this.book && this.book.project && this.book.project.ID ) {
      return this.book.project.ID;
    }
    const workId = this.book && (this.book as any).work_ID;
    return workId && Number(workId) > 0 ? Number(workId) : 0;
  }

  onConfirm(element: any): void {
    if ( this.moving || !this.book || !this.selectedProject ) {
      return;
    }
    this.moving = true;
    if ( element && element.classList ) { element.classList.add('spinner'); }

    // `currentProjectId` of 0 is accepted by the server for a Book that
    // is not in a Project yet — that is a supported case, not an error.
    this.projectService.moveBookToProject(this.book.ID, this.currentProjectId, this.selectedProject.ID).subscribe(
      response => {
        this.moving = false;
        if ( element && element.classList ) { element.classList.remove('spinner'); }
        // The endpoint answers with a `{message, status, data}` envelope
        // and reports refusals as `status: 'ERROR'` inside a 200, so a
        // failure is invisible unless the body is actually read.
        if ( response && (response as any).status === 'ERROR' ) {
          console.error('moveBookToProject refused', response);
          this.snackBar.open(LanguagePipe.get('The Book could not be moved.'),
            LanguagePipe.get('Dismiss', 'compressSpaces'), { duration: 6000 });
          return;
        }
        this.dialogRef.close(this.selectedProject);
      },
      err => {
        this.moving = false;
        if ( element && element.classList ) { element.classList.remove('spinner'); }
        console.error('moveBookToProject failed', err);
        this.snackBar.open(LanguagePipe.get('The Book could not be moved.'),
          LanguagePipe.get('Dismiss', 'compressSpaces'), { duration: 6000 });
      }
    );
  }

  get filteredProjects() {
    if (!this.filterByFolder || !this.selectedFolderNode) {
      return this.allProjects;
    }
    // Logic: Only show projects belonging to the selected FileNode
    return this.allProjects.filter(p => p.folder === this.selectedFolderNode.id);
  }
}
